import { Button } from 'designSystem/components/Button'
import { Separator } from 'designSystem/components/Separator'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import { Info } from 'lucide-react'
import { useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  type ForecastComponentConfig,
  type GeckoAssetModel,
  type GeckoComponentCoords,
  type GeckoComponentDataFrame,
  type GeckoInspection,
  type GeckoTMinMethod,
} from 'types'

import { useRemainingLifeCalculationData } from './remainingLifeCalculationData'

export const DamageMetric = {
  Nominal: 'nominal',
  Loss: 'loss',
  Thickness: 'thickness',
  Zero: 'zero',
} as const

export type DamageMetric = (typeof DamageMetric)[keyof typeof DamageMetric]

type DamageValues = {
  nominal?: number
  thickness?: number
  tmin?: number
}

const InspectorContainer = styled.div`
  border: 1px solid var(--border);
  border-radius: 0.375rem;
  background-color: color-mix(in oklab, var(--background) 70%, transparent);
  padding: 0.75rem;
  font-size: 0.75rem;
  line-height: 1rem;
`

const HeaderRow = styled.div`
  margin-bottom: 0.75rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`

const HeaderTitle = styled.h3`
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 600;
`

const HeaderMeta = styled.div`
  margin-top: 0.25rem;
  color: var(--muted-foreground);

  > * + * {
    margin-top: 0.125rem;
  }
`

const FormulaInfoButton = styled(Button)`
  border-radius: 9999px;
  padding: 0.125rem;
  color: var(--muted-foreground);

  &:hover {
    color: var(--foreground);
  }
`

const FormulaTooltipContent = styled(TooltipContent)`
  max-width: 20rem;
`

const InspectorSection = styled.section`
  margin-top: 1rem;
`

const SectionHeading = styled.h4`
  margin-bottom: 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  color: var(--muted-foreground);
`

const DamageLayout = styled.div`
  display: flex;
  gap: 1rem;
`

const DamageRowsArea = styled.div`
  position: relative;
  height: 10rem;
  min-width: 0;
  flex: 1 1 0%;
`

const StackFrame = styled.div`
  position: relative;
  height: 10rem;
  width: 2.75rem;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 0.125rem;
  border: 1px solid var(--border);
  background-color: color-mix(in oklab, var(--muted) 30%, transparent);
`

const LossSegment = styled.div<{ $selected: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  transition: background-color 0.15s;
  background-color: ${({ $selected }) =>
    $selected
      ? 'color-mix(in oklab, var(--primary) 70%, transparent)'
      : 'color-mix(in oklab, var(--muted-foreground) 40%, transparent)'};
  background-image: repeating-linear-gradient(
    -45deg,
    rgba(0, 0, 0, 0.55) 0,
    rgba(0, 0, 0, 0.55) 3px,
    transparent 3px,
    transparent 7px
  );
`

const MetalSegment = styled.div<{ $selected: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  transition: background-color 0.15s;
  background-color: ${({ $selected }) =>
    $selected
      ? 'color-mix(in oklab, var(--primary) 45%, transparent)'
      : 'color-mix(in oklab, var(--muted-foreground) 35%, transparent)'};
`

const StackLine = styled.div<{ $selected: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  transform: translateY(-50%);
  transition: background-color 0.15s;
  background-color: ${({ $selected }) =>
    $selected
      ? 'var(--primary)'
      : 'color-mix(in oklab, var(--border) 80%, transparent)'};
`

const DamageRowButton = styled.button<{ $selected: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  text-align: left;
  transition:
    background-color 0.15s,
    color 0.15s;
  background-color: ${({ $selected }) =>
    $selected ? 'var(--muted)' : 'transparent'};
  color: ${({ $selected }) => ($selected ? 'var(--foreground)' : 'inherit')};

  &:hover {
    background-color: ${({ $selected }) =>
      $selected
        ? 'var(--muted)'
        : 'color-mix(in oklab, var(--muted) 60%, transparent)'};
  }
`

const DamageRowLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--muted-foreground);
`

const DamageRowValue = styled.span`
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`

const RateList = styled.div`
  > * + * {
    margin-top: 0.25rem;
  }
`

const RateSeparator = styled(Separator)`
  margin-bottom: 1rem;
`

const CalculationRowContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(5.75rem, auto);
  align-items: center;
  gap: 0.75rem;
  border-radius: 0.25rem;
  padding: 0.375rem 0.5rem;
`

const CalculationValue = styled.div<{ $emphasis?: boolean }>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-weight: ${({ $emphasis }) => ($emphasis ? 600 : 'inherit')};
`

const CalculationDescription = styled.div<{ $emphasis?: boolean }>`
  text-align: right;
  font-weight: ${({ $emphasis }) => ($emphasis ? 600 : 'inherit')};
  color: ${({ $emphasis }) =>
    $emphasis ? 'var(--foreground)' : 'var(--muted-foreground)'};
`

export interface RemainingLifeCalculationInspectorProps {
  assetModel?: GeckoAssetModel
  componentId?: string
  inspection?: GeckoInspection
  comparisonInspection?: GeckoInspection
  thicknessCdf?: GeckoComponentDataFrame
  shortTermRateCdf?: GeckoComponentDataFrame
  hoveredCoords?: GeckoComponentCoords
  forecastComponentConfig?: ForecastComponentConfig
  tminMethod?: GeckoTMinMethod
}

export function RemainingLifeCalculationInspector(
  props: RemainingLifeCalculationInspectorProps
) {
  const data = useRemainingLifeCalculationData(props)
  const [selectedDamageMetric, setSelectedDamageMetric] =
    useState<DamageMetric | null>(null)

  return (
    <InspectorContainer>
      <HeaderRow>
        <div>
          <HeaderTitle>Calculation</HeaderTitle>
          <HeaderMeta>
            <div>{data.binSize} Bin</div>
            <div>Coords: {data.coordsLabel}</div>
          </HeaderMeta>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <FormulaInfoButton
              type='button'
              variant='ghost'
              size='icon-xs'
              aria-label='Corrosion rate formula'>
              <Info size={14} />
            </FormulaInfoButton>
          </TooltipTrigger>
          <FormulaTooltipContent side='left' align='start'>
            <p>{data.formulaHelp}</p>
          </FormulaTooltipContent>
        </Tooltip>
      </HeaderRow>

      <DamageBreakdown
        data={data}
        selectedMetric={selectedDamageMetric}
        onSelectedMetricChange={setSelectedDamageMetric}
      />

      <RateBreakdown data={data} />
    </InspectorContainer>
  )
}

function DamageBreakdown({
  data,
  selectedMetric,
  onSelectedMetricChange,
}: {
  data: ReturnType<typeof useRemainingLifeCalculationData>
  selectedMetric: DamageMetric | null
  onSelectedMetricChange: (metric: DamageMetric | null) => void
}) {
  const rowPositions = useMemo(
    () => getDamageRowPositions(data.damageValues),
    [data.damageValues]
  )
  const rows: Array<{ metric: DamageMetric; label: string; value: string }> = [
    { metric: DamageMetric.Nominal, label: 'Nominal', value: data.nominal },
    { metric: DamageMetric.Loss, label: 'Loss', value: data.loss },
    {
      metric: DamageMetric.Thickness,
      label: 'Thickness',
      value: data.thickness,
    },
    { metric: DamageMetric.Zero, label: 'Zero', value: data.zero },
  ]

  return (
    <InspectorSection>
      <SectionHeading>Damage</SectionHeading>
      <DamageLayout>
        <DamageStack
          values={data.damageValues}
          selectedMetric={selectedMetric}
        />
        <DamageRowsArea>
          {rows.map((row) => (
            <DamageRow
              key={row.metric}
              label={row.label}
              value={row.value}
              isSelected={selectedMetric === row.metric}
              position={rowPositions[row.metric]}
              onMouseEnter={() => onSelectedMetricChange(row.metric)}
              onMouseLeave={() => onSelectedMetricChange(null)}
            />
          ))}
        </DamageRowsArea>
      </DamageLayout>
    </InspectorSection>
  )
}

function DamageStack({
  values,
  selectedMetric,
}: {
  values: DamageValues
  selectedMetric: DamageMetric | null
}) {
  const segment = getDamageStackSegments(values)

  return (
    <StackFrame aria-hidden='true'>
      <LossSegment
        $selected={
          selectedMetric === DamageMetric.Nominal ||
          selectedMetric === DamageMetric.Loss
        }
        style={{
          top: `${segment.loss.top}%`,
          height: `${segment.loss.height}%`,
        }}
      />
      <MetalSegment
        $selected={selectedMetric === DamageMetric.Thickness}
        style={{
          top: `${segment.metal.top}%`,
          height: `${segment.metal.height}%`,
        }}
      />
      <StackLine
        $selected={selectedMetric === DamageMetric.Nominal}
        style={{ top: '0%' }}
      />
      <StackLine
        $selected={selectedMetric === DamageMetric.Thickness}
        style={{ top: `${segment.thicknessLine}%` }}
      />
      <StackLine
        $selected={selectedMetric === DamageMetric.Zero}
        style={{ top: '100%' }}
      />
    </StackFrame>
  )
}

function DamageRow({
  label,
  value,
  isSelected,
  position,
  onMouseEnter,
  onMouseLeave,
}: {
  label: string
  value: string
  isSelected: boolean
  position: number
  onMouseEnter: () => void
  onMouseLeave: () => void
}) {
  return (
    <DamageRowButton
      type='button'
      $selected={isSelected}
      style={{
        top: `${position}%`,
        transform: getDamageRowTransform(position),
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onMouseEnter}
      onBlur={onMouseLeave}>
      <DamageRowLabel>{label}</DamageRowLabel>
      <DamageRowValue>{value}</DamageRowValue>
    </DamageRowButton>
  )
}

function RateBreakdown({
  data,
}: {
  data: ReturnType<typeof useRemainingLifeCalculationData>
}) {
  return (
    <InspectorSection>
      <RateSeparator />
      <SectionHeading>Rate</SectionHeading>
      <RateList>
        <CalculationRow
          value={data.corrosionRate}
          description='Corrosion rate'
          emphasis
        />
        <CalculationRow value={data.age} description={data.ageLabel} />
        <CalculationRow
          value={data.inspectionDate}
          description={data.inspectionDateLabel}
        />
        <CalculationRow
          value={
            data.inServiceDateLabel === 'Prior'
              ? data.comparisonInspectionDate
              : data.inServiceDate
          }
          description={data.inServiceDateLabel}
        />
      </RateList>
    </InspectorSection>
  )
}

function CalculationRow({
  value,
  description,
  emphasis,
}: {
  value: string
  description: string
  emphasis?: boolean
}) {
  return (
    <CalculationRowContainer>
      <CalculationValue $emphasis={emphasis}>{value}</CalculationValue>
      <CalculationDescription $emphasis={emphasis}>
        {description}
      </CalculationDescription>
    </CalculationRowContainer>
  )
}

function getDamageStackSegments(values: DamageValues) {
  const maxValue = getDamageScaleMax(values)
  const position = (value: number | undefined, fallback: number) => {
    if (!Number.isFinite(value)) return fallback
    return clampPercent(((maxValue - value!) / maxValue) * 100)
  }
  const nominalLine = position(values.nominal, 0)
  const thicknessLine = position(values.thickness, 40)

  return {
    loss: getStackSegment(nominalLine, thicknessLine, { top: 0, height: 40 }),
    metal: getStackSegment(thicknessLine, 100, { top: 40, height: 60 }),
    thicknessLine,
  }
}

function getDamageRowPositions(
  values: DamageValues
): Record<DamageMetric, number> {
  const segment = getDamageStackSegments(values)
  return spreadDamageRows(
    {
      nominal: 0,
      loss: getSegmentMidpoint(segment.loss),
      thickness: segment.thicknessLine,
      zero: 100,
    },
    [
      DamageMetric.Nominal,
      DamageMetric.Loss,
      DamageMetric.Thickness,
      DamageMetric.Zero,
    ],
    30
  )
}

function spreadDamageRows(
  positions: Record<DamageMetric, number>,
  orderedMetrics: DamageMetric[],
  minGap: number
): Record<DamageMetric, number> {
  const nextPositions = { ...positions }
  const firstMetric = orderedMetrics.at(0)
  const lastMetric = orderedMetrics.at(-1)

  if (firstMetric === DamageMetric.Nominal) nextPositions.nominal = 0
  if (lastMetric === DamageMetric.Zero) nextPositions.zero = 100

  for (let index = 1; index < orderedMetrics.length; index += 1) {
    const previous = orderedMetrics[index - 1]
    const current = orderedMetrics[index]
    nextPositions[current] = Math.max(
      nextPositions[current],
      nextPositions[previous] + minGap
    )
  }

  if (lastMetric === DamageMetric.Zero) nextPositions.zero = 100

  for (let index = orderedMetrics.length - 2; index >= 0; index -= 1) {
    const current = orderedMetrics[index]
    const next = orderedMetrics[index + 1]
    nextPositions[current] = Math.min(
      nextPositions[current],
      nextPositions[next] - minGap
    )
  }

  if (firstMetric === DamageMetric.Nominal) nextPositions.nominal = 0
  if (lastMetric === DamageMetric.Zero) nextPositions.zero = 100

  return nextPositions
}

function getSegmentMidpoint(segment: { top: number; height: number }): number {
  return clampPercent(segment.top + segment.height / 2)
}

function getDamageRowTransform(position: number): string {
  if (position <= 0) return 'translateY(0)'
  if (position >= 100) return 'translateY(-100%)'
  return 'translateY(-50%)'
}

function getDamageScaleMax(values: DamageValues): number {
  let max = 0
  for (const value of [values.nominal, values.thickness, values.tmin]) {
    if (Number.isFinite(value) && value! > max) {
      max = value!
    }
  }
  return max > 0 ? max : 1
}

function getStackSegment(
  start: number,
  end: number,
  fallback: { top: number; height: number }
) {
  if (!Number.isFinite(start) || !Number.isFinite(end)) return fallback
  const top = Math.min(start, end)
  const height = Math.max(Math.abs(end - start), 1)
  return { top: clampPercent(top), height: clampPercent(height) }
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.max(0, Math.min(100, value))
}
